import React, { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { datadogRum } from '@datadog/browser-rum';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import { SarStore } from '@store/sar-store';
import { useRouter, ROUTES } from '../../providers/router';
import { SelectionList } from '../../components/selection-list';
import { useUserSelections } from '../../providers/user-selections';
import { Levels, SubCategory } from '../../constants';
import { SpinnerLocation } from '../spinner-location';
import { resolveStockNumber } from '../spinner-location/resolveStockNumber';

export const Locations = () => {
  const { completeFormStep } = useRouter();
  const { userSelections, userFeedData } = useUserSelections();
  const { isTreatment } = useSnapshot(SarStore).cosmeticExteriorLocationExperiment;

  // The spinner renders the customer's OWN vehicle: stock number from the feed
  // (?stock= query override wins, for demos). Without one — legacy inventory with no
  // stock upstream — the classic tile list shows instead of somebody else's car.
  const stockNumber = resolveStockNumber(userFeedData?.stockNumber);

  // Late fallback: the spinner MFE failed to load, or the vehicle has no usable 360°
  // frames on the CDN (sold long ago). SpinnerLocation reports it; we swap to the list.
  const [spinnerUnavailable, setSpinnerUnavailable] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);

  // Treatment + exterior cosmetic + a resolvable vehicle → spinner tap-to-mark;
  // control, Glass/Interior, or no stock → the existing tile list. The bucket is
  // fetched by router-provider before navigating here (mirrors the sibling S&R
  // experiments), so it reads synchronously.
  const useSpinner =
    isTreatment && userSelections.subcategory === SubCategory.Exterior && stockNumber != null && !spinnerUnavailable;

  useEffect(() => {
    if (isTreatment && userSelections.subcategory === SubCategory.Exterior && stockNumber == null) {
      // Treatment users who can't get the spinner — worth watching in Datadog.
      datadogRum.addAction('sar-spinner-no-stock-number', { vehicleId: userFeedData?.vehicleId });
      setFallbackReason('no-stock-number');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (useSpinner) return; // SpinnerLocation emits its own viewed event (variant: spinner)
    Analytics.trackEvent(AnalyticEvents.sarLocationViewed);
    datadogRum.addAction(AnalyticEvents.sarLocationViewed);
  }, [useSpinner]);

  // A treatment-eligible session got the classic list instead of the spinner (no stock number,
  // or the MFE/360° frames failed to load). Track it in clickstream to measure how often
  // treatment users don't actually get the spinner (treatment-arm dilution).
  useEffect(() => {
    if (!fallbackReason) return;
    Analytics.trackEvent(AnalyticEvents.sarLocationSpinnerFallback, { reason: fallbackReason });
    datadogRum.addAction(AnalyticEvents.sarLocationSpinnerFallback, { reason: fallbackReason });
  }, [fallbackReason]);

  const handleNextRoute = (dataSubset: any) => {
    completeFormStep(ROUTES.LOCATIONS, dataSubset);
  };

  if (useSpinner) {
    return (
      <SpinnerLocation
        stockNumber={stockNumber}
        handleNextRoute={handleNextRoute}
        onUnavailable={(reason) => {
          setSpinnerUnavailable(true);
          setFallbackReason((prev) => prev ?? reason);
        }}
      />
    );
  }

  return <SelectionList level={Levels.Location} handleNextRoute={handleNextRoute} />;
};

export default Locations;
